import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/authStore";

// Phase 5b: a `?next=` query is honored for post-login navigation so an
// unauthenticated hit on /linkforge/merch/* can bounce via /login and land
// back on the intended page. Validate the target is a same-origin relative
// path to defend against open-redirect abuse.
function _safeNext(raw) {
  if (!raw) return null;
  if (typeof raw !== "string") return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  try {
    const u = new URL(raw, "https://local.invalid");
    if (u.origin !== "https://local.invalid") return null;
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuth((s) => s.login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { user, scope } = await login(email.trim(), password);
      if (scope === "reset_only" || user.must_reset_password) {
        navigate("/reset-password", { replace: true });
      } else {
        // Priority: explicit ?next= (used by the ForgeLinc nginx bridge
        // redirect) > router-state `from` > default dashboard.
        const nextParam = _safeNext(new URLSearchParams(location.search).get("next"));
        if (nextParam) {
          // Use full-document navigation so the browser attaches the fresh
          // `cl_access_token` cookie on the top-level request — a router
          // nav would stay inside the SPA and never trigger the subrequest.
          window.location.assign(nextParam);
          return;
        }
        const to = location.state?.from || "/focus";
        navigate(to, { replace: true });
      }
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-brand" style={{ alignItems: "center" }}>
          <img
            src="/LincWerks2026Logo.png"
            alt="LincWerks"
            style={{ width: 92, height: 92, objectFit: "contain", marginRight: 4 }}
          />
          <div>
            <div className="wordmark" style={{ fontSize: 22 }}>
              <span className="linc">Linc</span>Werks
            </div>
            <div className="hint" style={{ marginTop: 2 }}>
              Build · Connect · Evolve
            </div>
          </div>
        </div>
        <div className="tag">A virtual company. One command center.</div>
        <h1>Sign in</h1>
        <p className="sub-tag">
          Direct your AI workforce. Every action is logged, every gate is enforced.
        </p>

        {err ? <div className="auth-error" data-testid="login-error">{err}</div> : null}

        <div className="form-row">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@linkworks.org"
            required
          />
        </div>
        <div className="form-row">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            data-testid="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button
          type="submit"
          className="btn primary"
          data-testid="login-submit"
          style={{ width: "100%", justifyContent: "center", marginTop: 6 }}
          disabled={busy}
        >
          {busy ? <span className="loader" /> : "Sign in"}
        </button>

        <div className="auth-note">
          LincWerks · CommandLinc · FocusLinc
        </div>
      </form>
    </div>
  );
}
